/**
 * MoversTab — five stacked ranked sections for the Research Desk Movers tab
 * (Steve directive 2026-05-22). Each list is top-N from /api/research-movers
 * with click-through to the per-stock StockStoryDrawer.
 *
 * Sections:
 *   1. Jump-to single-stock research (deep-links back to the Ideas tab)
 *   2. Composite score movers (Up + Down columns, 20-day move)
 *   3. Congressional Top Buys + Top Sells (90-day window)
 *   4. EPS revision Top Pos + Top Neg (universe-wide)
 *   5. Earnings-call transcript tone — Top Positive + Top Negative (scope)
 */
'use client';

import { useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { StockStoryDrawer } from '@/components/StockStoryDrawer';

interface Mover { symbol: string; name: string; sector?: string; composite?: number | null; compositePrior?: number | null; d20?: number | null }
interface CongRow { symbol: string; name: string; sector?: string; buy90d: number; sell90d: number; net90d: number; total90d: number }
interface RevRow {
  symbol: string;
  name: string;
  sector?: string;
  epsRevision: number | null;
  composite?: number | null;
  priorEps?: number | null;    // ~30d-ago eps1y (the baseline)
  currentEps?: number | null;  // today's eps1y (the revised)
}
interface AccelRow { symbol: string; name: string; sector?: string; accel: number | null; delta7d: number | null; delta30d: number | null; composite?: number | null }
interface TranscriptRow { symbol: string; name: string; sector?: string; tone: 'positive' | 'neutral' | 'cautious' | 'negative'; headline?: string; oneLiner?: string; period: string }
interface MoversResp {
  asOf: string;
  scope: { universeCount: number; transcriptScopeCount: number; transcriptScopeWithCovered: number };
  compositeMoversUp: Mover[];
  compositeMoversDown: Mover[];
  congressionalTopBuys: CongRow[];
  congressionalTopSells: CongRow[];
  epsRevisionTopPos: RevRow[];
  epsRevisionTopNeg: RevRow[];
  revisionAccelTopPos: AccelRow[];
  revisionAccelTopNeg: AccelRow[];
  transcriptTopPos: TranscriptRow[];
  transcriptTopNeg: TranscriptRow[];
}

const TONE_GLYPH: Record<TranscriptRow['tone'], { glyph: string; cls: string }> = {
  positive: { glyph: '+ve', cls: 'text-emerald-300' },
  neutral:  { glyph: '=',   cls: 'text-amber-300/70' },
  cautious: { glyph: '⚠',   cls: 'text-amber-300' },
  negative: { glyph: '-ve', cls: 'text-red-300' },
};

/**
 * Renders "was X → now Y (Δ Z)" with the derived delta as the headline (sign-tinted)
 * and the before/after numbers as the supporting caption so a reader can interpret
 * the Δ in context. Used across composite movers + factor-pillar leaderboards.
 */
function WasNowDelta({
  prior, now, delta, tone, decimals = 2,
}: {
  prior: number | null | undefined;
  now: number | null | undefined;
  delta: number | null | undefined;
  tone: 'up' | 'down';
  decimals?: number;
}) {
  const deltaCls = tone === 'up' ? 'text-emerald-300' : 'text-red-300';
  const fmt = (v: number | null | undefined) => v == null ? '—' : (v >= 0 ? '+' : '') + v.toFixed(decimals);
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className={`font-mono tabular-nums ${deltaCls}`}>{delta == null ? '—' : fmt(delta)}</span>
      <span className="text-[9px] font-mono tabular-nums text-[#64748b]">
        {fmt(prior)} → {fmt(now)}
      </span>
    </div>
  );
}

/**
 * Pair of decimal-percent windows ("Δ7 +X% · Δ30 +Y%") with a derived
 * acceleration headline. Sign of acceleration drives the headline color.
 */
function WindowedDelta({
  delta7d, delta30d, accel, tone,
}: {
  delta7d: number | null | undefined;
  delta30d: number | null | undefined;
  accel: number | null | undefined;
  tone: 'up' | 'down';
}) {
  const accelCls = tone === 'up' ? 'text-emerald-300' : 'text-red-300';
  const pct = (v: number | null | undefined) => v == null ? '—'
    : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
  const pp = (v: number | null | undefined) => v == null ? '—'
    : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}pp`;
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className={`font-mono tabular-nums ${accelCls}`}>{pp(accel)}</span>
      <span className="text-[9px] font-mono tabular-nums text-[#64748b]">
        Δ7 {pct(delta7d)} · Δ30 {pct(delta30d)}
      </span>
    </div>
  );
}

/**
 * EPS revision cell: headline % change with the raw $prior → $current
 * caption beneath. Steve directive 2026-05-22: "1 cent to 2 cents reads
 * as 100% and is irrelevant" — the raw EPS values let the reader see the
 * SCALE of the revision, not just the magnitude.
 */
function EpsRevisionCell({ row, tone }: { row: RevRow; tone: 'up' | 'down' }) {
  const pctCls = tone === 'up' ? 'text-emerald-300' : 'text-red-300';
  const pctTxt = row.epsRevision != null
    ? `${row.epsRevision >= 0 ? '+' : ''}${(row.epsRevision * 100).toFixed(1)}%`
    : '—';
  const fmtEps = (v: number | null | undefined) => v == null ? '—' : `$${v.toFixed(2)}`;
  const hasRaw = row.priorEps != null || row.currentEps != null;
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className={`font-mono tabular-nums ${pctCls}`}>{pctTxt}</span>
      {hasRaw && (
        <span className="text-[9px] font-mono tabular-nums text-[#64748b]">
          {fmtEps(row.priorEps)} → {fmtEps(row.currentEps)}
        </span>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[12px] uppercase tracking-wider font-semibold text-[#cbd5e1]">{title}</h2>
        {subtitle && <span className="text-[10px] text-[#64748b]">{subtitle}</span>}
      </div>
      {children}
    </section>
  );
}

function RankedRow({
  rank, symbol, name, right, sub, onClick,
}: {
  rank: number;
  symbol: string;
  name: string;
  right: React.ReactNode;
  sub?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left grid grid-cols-[24px_60px_1fr_auto] gap-2 items-baseline px-2 py-1.5 rounded hover:bg-[#1e293b]/40 transition-colors"
      >
        <span className="text-[10px] text-[#64748b] tabular-nums">#{rank}</span>
        <span className="text-[12px] font-mono font-bold text-amber-300">{symbol}</span>
        <span className="text-[11px] text-[#cbd5e1] truncate">
          {name}
          {sub && <span className="text-[10px] text-[#64748b] ml-2">{sub}</span>}
        </span>
        <span className="text-[11px] tabular-nums font-mono">{right}</span>
      </button>
    </li>
  );
}

function TwoColList({ left, right, leftLabel, rightLabel }: { left: React.ReactNode; right: React.ReactNode; leftLabel: string; rightLabel: string }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="border border-[#1e293b] bg-[#0f141c] rounded p-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300/70 px-2 mb-1">{leftLabel}</div>
        <ul className="space-y-0.5">{left}</ul>
      </div>
      <div className="border border-[#1e293b] bg-[#0f141c] rounded p-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-red-300/70 px-2 mb-1">{rightLabel}</div>
        <ul className="space-y-0.5">{right}</ul>
      </div>
    </div>
  );
}

export function MoversTab() {
  const { data, loading } = useResource<MoversResp>('/api/research-movers', 60_000);
  const [drawerSymbol, setDrawerSymbol] = useState<string | null>(null);

  if (loading) return <div className="text-[#94a3b8] py-8">Loading movers…</div>;
  if (!data) return <div className="text-[#94a3b8] py-8">No movers data.</div>;

  const open = (sym: string) => setDrawerSymbol(sym);

  return (
    <div className="space-y-6">
      <div className="text-[10px] text-[#64748b]">
        Universe: <span className="text-[#cbd5e1] tabular-nums">{data.scope.universeCount}</span> stocks
        {' · '}Transcript & Congressional scope:{' '}
        <span className="text-[#cbd5e1] tabular-nums">{data.scope.transcriptScopeCount}</span>
        {' '}(portfolio ∪ alpha-basket)
        {' · '}with transcript coverage:{' '}
        <span className="text-[#cbd5e1] tabular-nums">{data.scope.transcriptScopeWithCovered}</span>
      </div>

      {/* 1) Composite score movers — universe-wide, 20-day */}
      <Section
        title="Composite score movers (20-day)"
        subtitle={`Top ${data.compositeMoversUp.length} of ${data.scope.universeCount}`}
      >
        <TwoColList
          leftLabel="Biggest moves up"
          rightLabel="Biggest moves down"
          left={data.compositeMoversUp.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={<WasNowDelta prior={r.compositePrior} now={r.composite} delta={r.d20} tone="up" />}
              onClick={() => open(r.symbol)}
            />
          ))}
          right={data.compositeMoversDown.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={<WasNowDelta prior={r.compositePrior} now={r.composite} delta={r.d20} tone="down" />}
              onClick={() => open(r.symbol)}
            />
          ))}
        />
      </Section>

      {/* 3) Congressional top buys + top sells across scope — ranked by NET.
          Steve directive 2026-05-22: gross-rank put AMZN in BOTH lists
          (high gross-buy AND high gross-sell). Net is the right question:
          which way did Congress lean on this name. Display shows NET as
          the headline with the gross breakdown underneath. */}
      <Section
        title="Congressional trading (last 90 days)"
        subtitle="Ranked by NET (buys − sells). Scope = universe. Click for full disclosures."
      >
        <TwoColList
          leftLabel="Top net buys"
          rightLabel="Top net sells"
          left={data.congressionalTopBuys.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={
                <div className="flex flex-col items-end leading-tight">
                  <span className="font-mono tabular-nums text-emerald-300">+{r.net90d}</span>
                  <span className="text-[9px] font-mono tabular-nums text-[#64748b]">{r.buy90d}B / {r.sell90d}S</span>
                </div>
              }
              onClick={() => open(r.symbol)}
            />
          ))}
          right={data.congressionalTopSells.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={
                <div className="flex flex-col items-end leading-tight">
                  <span className="font-mono tabular-nums text-red-300">{r.net90d}</span>
                  <span className="text-[9px] font-mono tabular-nums text-[#64748b]">{r.buy90d}B / {r.sell90d}S</span>
                </div>
              }
              onClick={() => open(r.symbol)}
            />
          ))}
        />
      </Section>

      {/* 4) EPS revision extremes — universe-wide */}
      <Section
        title="Analyst EPS revisions (% change since ~30d-ago baseline)"
        subtitle={`Universe-wide. ${data.scope.universeCount} stocks evaluated. Each row shows the raw $X → $Y move underneath so 1¢-to-2¢ noise reads as obviously small.`}
      >
        <TwoColList
          leftLabel="Strongest upward revisions"
          rightLabel="Strongest downward revisions"
          left={data.epsRevisionTopPos.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={<EpsRevisionCell row={r} tone="up" />}
              onClick={() => open(r.symbol)}
            />
          ))}
          right={data.epsRevisionTopNeg.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={<EpsRevisionCell row={r} tone="down" />}
              onClick={() => open(r.symbol)}
            />
          ))}
        />
      </Section>

      {/* 4b) Revision acceleration leaders — Δ7 − Δ30 (2026-05-22) */}
      <Section
        title="Revision acceleration (Δ7 − Δ30)"
        subtitle="Recent-week revisions running ahead of (or behind) the monthly trend. Headline pp = accel; Δ7 / Δ30 below show the components that produced it."
      >
        <TwoColList
          leftLabel="Accelerating up"
          rightLabel="Decelerating / accelerating down"
          left={data.revisionAccelTopPos.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={<WindowedDelta delta7d={r.delta7d} delta30d={r.delta30d} accel={r.accel} tone="up" />}
              onClick={() => open(r.symbol)}
            />
          ))}
          right={data.revisionAccelTopNeg.map((r, i) => (
            <RankedRow
              key={r.symbol}
              rank={i + 1}
              symbol={r.symbol}
              name={r.name}
              sub={r.sector}
              right={<WindowedDelta delta7d={r.delta7d} delta30d={r.delta30d} accel={r.accel} tone="down" />}
              onClick={() => open(r.symbol)}
            />
          ))}
        />
      </Section>

      {/* 5) Earnings-call transcript tones — scope */}
      <Section
        title="Earnings-call transcript tone"
        subtitle={`Scope = portfolio ∪ alpha-basket. ${data.scope.transcriptScopeWithCovered}/${data.scope.transcriptScopeCount} with summaries.`}
      >
        <TwoColList
          leftLabel="Most positive"
          rightLabel="Most cautious / negative"
          left={data.transcriptTopPos.map((r, i) => (
            <li key={r.symbol}>
              <button
                type="button"
                onClick={() => open(r.symbol)}
                className="w-full text-left grid grid-cols-[24px_60px_1fr_50px] gap-2 items-baseline px-2 py-1.5 rounded hover:bg-[#1e293b]/40 transition-colors"
              >
                <span className="text-[10px] text-[#64748b] tabular-nums">#{i + 1}</span>
                <span className="text-[12px] font-mono font-bold text-amber-300">{r.symbol}</span>
                <span className="text-[11px] text-[#cbd5e1] truncate">
                  {r.oneLiner ?? r.headline ?? r.name}
                </span>
                <span className={`text-[10px] font-mono uppercase text-right ${TONE_GLYPH[r.tone].cls}`}>{TONE_GLYPH[r.tone].glyph}</span>
              </button>
            </li>
          ))}
          right={data.transcriptTopNeg.map((r, i) => (
            <li key={r.symbol}>
              <button
                type="button"
                onClick={() => open(r.symbol)}
                className="w-full text-left grid grid-cols-[24px_60px_1fr_50px] gap-2 items-baseline px-2 py-1.5 rounded hover:bg-[#1e293b]/40 transition-colors"
              >
                <span className="text-[10px] text-[#64748b] tabular-nums">#{i + 1}</span>
                <span className="text-[12px] font-mono font-bold text-amber-300">{r.symbol}</span>
                <span className="text-[11px] text-[#cbd5e1] truncate">
                  {r.oneLiner ?? r.headline ?? r.name}
                </span>
                <span className={`text-[10px] font-mono uppercase text-right ${TONE_GLYPH[r.tone].cls}`}>{TONE_GLYPH[r.tone].glyph}</span>
              </button>
            </li>
          ))}
        />
      </Section>

      {drawerSymbol && (
        <StockStoryDrawer symbol={drawerSymbol} onClose={() => setDrawerSymbol(null)} />
      )}
    </div>
  );
}
